/**
 * 趋势图表组件 - 折线图/柱状图/堆叠面积图
 * 遵循 Opinion 图表开发规范
 */
import React, { useContext, useMemo } from 'react';
import EChartCommon from '@op/components/EChartCommon';
import type { TRequestBackType } from '@op/utils/requestUtil';
import {
  formatChartParamsDate,
  textShowImgElement,
} from '@op/components/ChartCommon/formatter';
import { DBO_EC, DBO_V } from '@op/utils/format';
import { chartAllColors, channelColorMap } from '@op/constants';
import {
  channelLegendRichInfo,
  dealwithChannelChartKey,
  channelInfo,
} from '@op/components/ChannelLabel';
import useLanguage from '@op/hooks/useLanguage';
import { useChartFootBtn } from '@op/hooks/useChartFootBtn';
import { ClassSpecial } from '@op/constants/common';
import { SecurityWeeklyReportContext } from '../../context';

type TChartType = 'line' | 'bar';

interface ITrendChartProps {
  data?: TRequestBackType;
  chartType: TChartType;
  dimension: 'language' | 'channel';
  getDisplayName: (name: string) => string;
  filter?: any;
}

const TrendChart: React.FC<ITrendChartProps> = ({
  data,
  chartType,
  dimension,
  getDisplayName,
  filter = {},
}) => {
  const { formatMessage } = useLanguage();
  const { toFeeds } = useContext(SecurityWeeklyReportContext);
  // 使用 useChartFootBtn 生成底部按钮
  const { getBottomClickBtn, divElementRef } = useChartFootBtn({
    filter,
    buttons: [
      {
        key: 'feeds',
        labelId: 'Click to go to [Feeds]',
      },
    ],
    onClick: () => toFeeds(),
  });

  // 创建渠道图标 rich 配置
  const createChannelRichText = useMemo(() => {
    if (dimension !== 'channel' || !data?.legend) return {};
    return channelLegendRichInfo(16, 16);
  }, [dimension, data?.legend]);

  // 图例格式化（渠道需要图标）
  const legendFormatter = useMemo(() => {
    if (dimension !== 'channel') {
      return (name: string) => {
        return getDisplayName(name);
      };
    }

    return (name: string) => {
      const key = dealwithChannelChartKey(name.toLowerCase());
      return `{${key}|} {name|${getDisplayName(name)}}`;
    };
  }, [dimension]);

  const option = useMemo(() => {
    if (!data || data.isEmpty) return {};

    // 通用配置
    const commonConfig = {
      color: chartAllColors,
      grid: { top: 20 },
      tooltip: {
        trigger: 'axis' as const,
        className: ClassSpecial.CHART_TOOLTIP_SCROLL,
        enterable: true,
        confine: false, // 允许 tooltip 超出图表区域
        // 智能定位：始终显示在鼠标正上方，方便垂直向上进入
        position: (point: any, params: any, dom: any, rect: any, size: any) =>
          DBO_EC.enterablePosition(point, params, dom, rect, size, {
            bottomOffset: 100,
          }),
        formatter: (params: any) => {
          return formatChartParamsDate({
            params,
            isSort: true,
            valueFormat: (value: number) => DBO_V.reviews(value),
            // 自定义左侧标题显示：渠道显示图标，语种直接显示
            textShowFun: (marker: string, seriesName: string, _data: any) => {
              if (dimension === 'channel') {
                // 渠道需要显示图标，使用 textShowImgElement
                const info = channelInfo(seriesName.toLocaleLowerCase() as any);
                return textShowImgElement(
                  marker,
                  getDisplayName(seriesName),
                  info.img,
                );
              }
              // 语种直接显示
              return `<span style="color:#D8DAE4;">${marker} ${getDisplayName(
                seriesName,
              )}</span>`;
            },
            // 添加 Sum 行，计算占比
            dealwithRecords: (records: any, total: number) => {
              // 计算每条记录的占比
              const recordsWithRatio = records.map((record: any) => ({
                ...record,
                ratioText: DBO_V.percentage(
                  record.originValue,
                  total,
                  2,
                  '-',
                  '-',
                ),
              }));

              return [
                {
                  text: formatMessage('Sum'),
                  value: DBO_V.reviews(total),
                  ratioText: total ? '100%' : '-',
                  originValue: total,
                },
                ...recordsWithRatio,
              ];
            },
            tablesConfigFunc: (records: any) => [
              {
                showHeader: true,
                columns: [
                  {
                    key: 'text',
                    header: DBO_EC.toolTime(
                      params[0]?.axisValue,
                      filter.date_type || 'daily',
                    ),
                  },
                  {
                    key: 'value',
                    align: 'right',
                    weight: 'bold',
                    header: formatMessage({ id: 'Mentions' }),
                  },
                  {
                    key: 'ratioText',
                    align: 'right',
                    weight: 'bold',
                    header: formatMessage({ id: 'Share-1' }),
                  },
                ],
                records,
              },
            ],
            footerText: getBottomClickBtn({
              StartTime: params[0]?.axisValue || '',
              EndTime: params[0]?.axisValue || '',
            }),
            className: 'chart-formatter-custom-footer',
          });
        },
      },
      legend: {
        data: data.legend,
        formatter: legendFormatter,
        textStyle:
          dimension === 'channel'
            ? { rich: createChannelRichText }
            : { fontSize: 12 },
      },
      xAxis: {
        type: 'category' as const,
        data: data.xAxis,
        axisLabel: {
          formatter: (v: string) => DBO_EC.timeX(v, filter.date_type),
        },
        axisLine: { lineStyle: { color: '#E5E5E5' } },
        axisTick: { show: false },
      },
      yAxis: {
        type: 'value' as const,
        axisLabel: { formatter: DBO_EC.reviews },
        axisLine: { show: false },
        splitLine: { lineStyle: { color: '#F0F0F0' } },
      },
    };

    // 获取颜色的辅助函数（渠道使用专属颜色）
    const getColor = (item: any, index: number) => {
      if (dimension === 'channel') {
        const channelKey =
          item.name.toLowerCase() as keyof typeof channelColorMap;
        return (
          channelColorMap[channelKey] ||
          chartAllColors[index % chartAllColors.length]
        );
      }
      return chartAllColors[index % chartAllColors.length];
    };

    // 柱状图特有配置（分组柱状图）
    if (chartType === 'bar') {
      return {
        ...commonConfig,
        tooltip: { ...commonConfig.tooltip, axisPointer: { type: 'shadow' } },
        series:
          data.series?.map((item, index) => ({
            name: item.name,
            type: 'bar' as const,
            data: item.data,
            stack: 'total', // 堆叠
            itemStyle: { color: getColor(item, index) },
          })) || [],
      };
    }

    // 折线图（默认）
    return {
      ...commonConfig,
      series:
        data.series?.map((item, index) => ({
          name: item.name,
          type: 'line' as const,
          data: item.data,
          itemStyle: { color: getColor(item, index) },
        })) || [],
    };
  }, [
    data,
    chartType,
    dimension,
    getDisplayName,
    createChannelRichText,
    legendFormatter,
    getBottomClickBtn,
  ]);

  return (
    <div ref={divElementRef} style={{ position: 'relative' }}>
      <EChartCommon
        isEmpty={data?.isEmpty}
        options={option}
        height={350}
        optionsProps={{ type: chartType }}
      />
    </div>
  );
};

export default TrendChart;
